/**
 * here we are generation 4 divs, that will cover entire page, except highlighted area
 * their position looks like this:
 *
 * 4 4 1 1 1 1 1 1 1
 * 4 4 1 1 1 1 1 1 1
 * 4 4 # # # 2 2 2 2
 * 4 4 # # # 2 2 2 2
 * 3 3 3 3 3 2 2 2 2
 * 3 3 3 3 3 2 2 2 2
 *
 * where 1...4 - are 4 divs, # - is highlighted area
 *
 * @param maskPositionX - start of mask X
 * @param maskPositionY - start of mask Y
 * @param maskWidth - mask width
 * @param maskHeight - mask height
 * @returns {HTMLDivElement}
 */
function generateOutsideClickBlocker ({
  maskPositionX,
  maskPositionY,
  maskWidth,
  maskHeight
}) {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const container = document.createElement('div');
  container.style.zIndex = 'calc(var(--popover-z-index) + 1)';
  container.style.position = 'absolute';
  container.style.top = '0';
  container.style.left = '0';


  const topBlocker = document.createElement('div');
  topBlocker.style.position = 'absolute';
  topBlocker.style.width = `${screenWidth - maskPositionX}px`;
  topBlocker.style.height = `${maskPositionY}px`;
  topBlocker.style.top = '0';
  topBlocker.style.left = `${maskPositionX}px`;

  const rightBlocker = document.createElement('div');
  rightBlocker.style.position = 'absolute';
  rightBlocker.style.width = `${screenWidth - maskPositionX - maskWidth}px`;
  rightBlocker.style.height = `${screenHeight - maskPositionY}px`;
  rightBlocker.style.top = `${maskPositionY}px`;
  rightBlocker.style.left = `${maskPositionX + maskWidth}px`;

  const bottomBlocker = document.createElement('div');
  bottomBlocker.style.position = 'absolute';
  bottomBlocker.style.width = `${maskPositionX + maskWidth}px`;
  bottomBlocker.style.height = `${screenHeight - maskPositionY - maskHeight}px`;
  bottomBlocker.style.top = `${maskPositionY + maskHeight}px`;
  bottomBlocker.style.left = '0';

  const leftBlocker = document.createElement('div');
  leftBlocker.style.position = 'absolute';
  leftBlocker.style.width = `${maskPositionX}px`;
  leftBlocker.style.height = `${maskPositionY + maskHeight}px`;
  leftBlocker.style.top = '0';
  leftBlocker.style.left = '0';

  container.appendChild(topBlocker);
  container.appendChild(rightBlocker);
  container.appendChild(bottomBlocker);
  container.appendChild(leftBlocker);
  return container;
}

function stopPropagation (e) {
  e.stopPropagation();
}

export default function createHighlight (target, {
  maskSizeOffsetX = 0,
  maskSizeOffsetY = 0,
  maskBorder = 0,
  shape = 'circle',
  allowClickInside = false,
  notTargetCenter = false,
  height = undefined,
  width = undefined,
  overflowBoundariesEl = ''
} = {}) {
  const boundingBox = target.getBoundingClientRect();

  // limit highlight zone by other elements boundary (should be used when element has scroll)
  if (overflowBoundariesEl) {
    const overflowEl = document.querySelector(overflowBoundariesEl);
    if (!overflowEl) {
      console.warn('[createHighlight] Overflow element not found');
    } else {
      const overflowBoundingBox = overflowEl.getBoundingClientRect();

      // limit start points of highlight
      boundingBox.x = Math.max(overflowBoundingBox.x, boundingBox.x);
      boundingBox.y = Math.max(overflowBoundingBox.y, boundingBox.y);

      // limit end points of highlight
      const widthLimit = overflowBoundingBox.width - (boundingBox.x - overflowBoundingBox.x);
      const heightLimit = overflowBoundingBox.height - (boundingBox.y - overflowBoundingBox.y);

      boundingBox.width = Math.min(boundingBox.width, widthLimit);
      boundingBox.height = Math.min(boundingBox.height, heightLimit);
    }
  }

  const targetCenterX = boundingBox.x + boundingBox.width / 2;
  const targetCenterY = boundingBox.y + boundingBox.height / 2;

  let maskWidth = width ?? boundingBox.width + maskSizeOffsetX * 2;
  let maskHeight = height ?? boundingBox.height + maskSizeOffsetY * 2;

  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  let maskPositionX = targetCenterX - (boundingBox.width + maskSizeOffsetX * 2) / 2;
  let maskPositionY = notTargetCenter
    ? scrollTop + boundingBox.y
    : scrollTop + targetCenterY - maskHeight / 2;

  let offsetX = maskSizeOffsetX;
  let offsetY = maskSizeOffsetY;

  let borderRadius = '2px';

  if (shape === 'circle') {
    const biggestSize = Math.max(maskHeight, maskWidth);

    if (maskWidth < maskHeight) {
      offsetX = offsetX + (biggestSize - maskWidth) / 2;
    } else if (maskHeight < maskWidth) {
      offsetY = offsetY + (biggestSize - maskHeight) / 2;
    }

    maskWidth = biggestSize;
    maskHeight = biggestSize;

    maskPositionX = targetCenterX - biggestSize / 2;
    maskPositionY = targetCenterY - biggestSize / 2;

    borderRadius = '50%';
  }

  const mask = document.createElement('div');

  mask.style.zIndex = 'calc(var(--popover-z-index) + 1)';
  mask.style.position = 'absolute';
  mask.style.width = `${maskWidth}px`;
  mask.style.height = `${maskHeight}px`;
  mask.style.top = `${maskPositionY}px`;
  mask.style.left = `${maskPositionX}px`;
  mask.style.borderRadius = borderRadius;

  // this oversized shadow creates tinted background around highlighted area
  mask.style.boxShadow = '0px 0px 0px 99999px rgba(206, 227, 248, 0.3)';

  if (maskBorder) {
    mask.style.border = `${maskBorder}px solid rgba(85, 133, 193, 0.6)`;
    mask.style.boxSizing = 'border-box';
  }

  if (allowClickInside) {
    mask.style.pointerEvents = 'none';
  }

  document.body.appendChild(mask);

  const clickBlockers = generateOutsideClickBlocker({
    maskPositionX,
    maskPositionY,
    maskWidth,
    maskHeight
  });

  document.body.appendChild(clickBlockers);

  clickBlockers.addEventListener('click', stopPropagation);
  if (!allowClickInside) {
    mask.addEventListener('click', stopPropagation);
  }

  function remove () {
    clickBlockers.removeEventListener('click', stopPropagation);
    mask.removeEventListener('click', stopPropagation);
    mask.remove();
    clickBlockers.remove();
  }

  function toggle (needShow) {
    mask.style.display = needShow ? 'block' : 'none';
    clickBlockers.style.display = needShow ? 'block' : 'none';
  }

  return {mask, remove, toggle, offsetX, offsetY};
}
